/**
 * useAnalyticsSnapshots — read latest analytics_snapshots rows per cluster.
 * RLS allows founders/admins only.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type SnapshotCluster = "growth" | "engagement" | "safety" | "revenue";

export interface AnalyticsSnapshot {
  id: string;
  taken_at: string;
  cluster: SnapshotCluster;
  metrics: Record<string, unknown>;
}

export const SNAPSHOT_CLUSTERS: SnapshotCluster[] = [
  "growth",
  "engagement",
  "safety",
  "revenue",
];

export const SNAPSHOT_LABELS: Record<SnapshotCluster, string> = {
  growth: "Growth",
  engagement: "Engagement",
  safety: "Safety",
  revenue: "Revenue",
};

export function useAnalyticsSnapshots(cluster: SnapshotCluster, limit = 30) {
  const [rows, setRows] = useState<AnalyticsSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("analytics_snapshots")
      .select("*")
      .eq("cluster", cluster)
      .order("taken_at", { ascending: false })
      .limit(limit);
    if (error) setError(error.message);
    setRows((data as AnalyticsSnapshot[] | null) ?? []);
    setLoading(false);
  }, [cluster, limit]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { rows, loading, error, reload };
}
